// src/index.ts
import { z } from "zod";
var safePublicAssetUrlSchema = z.string().refine(
  (value) => value.startsWith("/api/v1/branding-assets/") || value.startsWith("/api/v1/admin/branding-assets/") || value.startsWith("/api/v1/product-images/") || value.startsWith("https://") || value.startsWith("http://")
);
var backgroundVideoUrlSchema = z.string().max(2048).refine((value) => value.startsWith("https://") && URL.canParse(value));
var hexColorSchema = z.string().regex(/^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/i);
var httpUrlSchema = z.url().refine((value) => value.startsWith("https://") || value.startsWith("http://"));
var objectIdSchema = z.string().regex(/^[a-f0-9]{24}$/);
var signupIdSchema = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
var discordSnowflakeSchema = z.string().regex(/^\d{15,22}$/);
var communityFeatures = [
  "members",
  "bot_profile",
  "polls",
  "saved_posts",
  "mcp",
  "gateway"
];
var serviceNameSchema = z.enum(["customer-api", "admin-api", "dev-gateway"]);
var healthResponseSchema = z.object({
  service: serviceNameSchema,
  status: z.literal("ok"),
  timestamp: z.iso.datetime(),
  version: z.string().min(1)
});
var emptyOrPattern = (pattern, message) => z.union([z.literal(""), z.string().trim().regex(pattern, message)]);
var trackingPixelsSchema = z.object({
  google: emptyOrPattern(
    /^(?:(?:G|AW|GT|GTM)-[A-Z0-9-]{4,32}|UA-\d{4,16}-\d{1,4})$/i,
    "Enter a valid Google tag ID."
  ),
  meta: emptyOrPattern(/^\d{5,32}$/, "Enter a valid Meta Pixel ID."),
  tiktok: emptyOrPattern(/^[A-Z0-9]{5,64}$/i, "Enter a valid TikTok Pixel ID.")
});
var tiktokTrackingEventSchema = z.object({
  name: z.enum(["CompletePayment", "InitiateCheckout"]),
  pixelId: z.string().regex(/^[A-Z0-9]{5,64}$/i)
}).nullable();
var customerContextSchema = z.object({
  analytics: trackingPixelsSchema,
  organizationId: z.string().min(1),
  organizationName: z.string().min(1),
  primaryColor: hexColorSchema
});
var customerViewerSchema = z.object({
  avatarUrl: z.url().nullable(),
  discordId: z.string().min(1),
  displayName: z.string().min(1),
  membership: z.object({
    bound: z.boolean(),
    email: z.email().nullable(),
    key: z.string().min(1).nullable()
  })
});
var customerSessionResponseSchema = z.discriminatedUnion("authenticated", [
  z.object({
    authenticated: z.literal(true),
    viewer: customerViewerSchema
  }),
  z.object({
    authenticated: z.literal(false),
    authorizationUrl: z.string().refine((value) => value.startsWith("/") || URL.canParse(value))
  })
]);
var safeCustomerDestinationSchema = z.string().refine((value) => value.startsWith("/") && !value.startsWith("//") || URL.canParse(value));
var customerDashboardProductOptionSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  name: z.string().min(1),
  planId: z.string().min(1),
  priceMinor: z.number().int().nonnegative()
});
var customerDashboardAccountSchema = z.object({
  mode: z.literal("live"),
  organization: z.object({
    billingProvider: z.literal("stripe"),
    logoUrl: safePublicAssetUrlSchema.nullable(),
    name: z.string().min(1),
    primaryColor: hexColorSchema,
    publishableKey: z.string().min(1).nullable(),
    secondaryColor: hexColorSchema,
    sidebar: z.array(
      z.object({
        name: z.string().min(1),
        url: safeCustomerDestinationSchema
      })
    ),
    stripeAccountId: z.string().min(1).nullable()
  }),
  viewer: z.object({
    avatarUrl: z.url().nullable(),
    displayName: z.string().min(1)
  }),
  member: z.discriminatedUnion("bound", [
    z.object({
      bound: z.literal(false)
    }),
    z.object({
      billing: z.object({
        addOns: z.array(customerDashboardProductOptionSchema),
        defaultPaymentMethod: z.object({
          brand: z.string().min(1),
          id: z.string().min(1),
          last4: z.string().regex(/^\d{4}$/)
        }).nullable(),
        invoices: z.array(
          z.object({
            createdAt: z.iso.datetime(),
            currency: z.string().regex(/^[A-Z]{3}$/),
            hostedUrl: z.url(),
            id: z.string().min(1),
            totalMinor: z.number().int()
          })
        ),
        paymentMethods: z.array(
          z.object({
            brand: z.string().min(1),
            id: z.string().min(1),
            last4: z.string().regex(/^\d{4}$/)
          })
        ),
        provider: z.literal("stripe"),
        publishableKey: z.string().min(1).nullable(),
        subscriptions: z.array(
          z.object({
            cancelAtPeriodEnd: z.boolean(),
            currency: z.string().regex(/^[A-Z]{3}$/),
            currentPeriodEnd: z.iso.datetime(),
            id: z.string().min(1),
            interval: z.enum(["day", "week", "month", "year"]),
            plans: z.array(
              z.object({
                currency: z.string().regex(/^[A-Z]{3}$/),
                id: z.string().min(1),
                name: z.string().min(1),
                priceMinor: z.number().int().nonnegative()
              })
            ),
            status: z.enum(["active", "canceled", "canceling", "past_due", "trialing", "unpaid"]),
            totalMinor: z.number().int().nonnegative(),
            trialEnd: z.iso.datetime().nullable(),
            upgrades: z.array(customerDashboardProductOptionSchema)
          })
        )
      }),
      bound: z.literal(true),
      email: z.email(),
      key: z.string().min(1),
      referrals: z.object({
        code: z.string().min(1),
        count: z.number().int().nonnegative(),
        rewards: z.array(
          z.object({
            creditAmountMinor: z.number().int().nonnegative().nullable(),
            eligible: z.boolean(),
            id: z.string().min(1),
            name: z.string().min(1),
            referralsRequired: z.number().int().nonnegative()
          })
        )
      })
    })
  ])
});
var customerOrderCompletionSchema = z.object({
  analytics: trackingPixelsSchema,
  branding: z.object({
    logoUrl: z.url().nullable(),
    name: z.string().min(1),
    primaryColor: hexColorSchema,
    secondaryColor: hexColorSchema
  }),
  conversionEvent: tiktokTrackingEventSchema.default(null)
});
var billingStripeEventTypeSchema = z.enum([
  "account.external_account.created",
  "charge.succeeded",
  "customer.subscription.deleted",
  "customer.subscription.updated",
  "invoice.payment_action_required",
  "invoice.payment_failed",
  "invoice.payment_succeeded",
  "invoice.voided"
]);
var billingStripeEventSchema = z.object({
  account: z.string().trim().regex(/^acct_[A-Za-z0-9_]+$/).optional(),
  created: z.number().int().nonnegative(),
  data: z.object({
    object: z.record(z.string(), z.unknown())
  }),
  id: z.string().trim().regex(/^evt_[A-Za-z0-9_]+$/),
  livemode: z.boolean(),
  type: billingStripeEventTypeSchema
});
var billingWebhookResponseSchema = z.object({
  received: z.literal(true),
  status: z.enum(["duplicate", "ignored", "processed"])
});
var customerMembershipKeyRequestSchema = z.object({
  key: z.string().trim().min(1, "Enter a membership key.").max(256)
});
var customerMembershipActionResponseSchema = z.object({
  action: z.enum(["bind", "join", "unbind"]),
  joined: z.boolean(),
  message: z.string().min(1),
  serverId: z.string().min(1).nullable(),
  success: z.literal(true)
});
var customerTerminalAccessRequestSchema = z.object({
  discordId: z.string().trim().regex(/^\d{15,22}$/, "Provide a valid Discord user ID.")
});
var customerTerminalAccessResponseSchema = z.object({
  allowed: z.boolean(),
  discordId: customerTerminalAccessRequestSchema.shape.discordId
});
var customerPaymentMethodRequestSchema = z.object({
  paymentMethodId: z.string().trim().regex(/^pm_[A-Za-z0-9_]+$/, "Select a valid payment method.")
});
var customerPaymentMethodSetupSchema = z.object({
  clientSecret: z.string().min(1),
  fixturePaymentMethodId: z.string().min(1).nullable()
});
var customerPaymentMethodActionResponseSchema = z.object({
  action: z.enum(["add", "default", "delete"]),
  message: z.string().min(1),
  success: z.literal(true)
});
var customerSubscriptionIdSchema = z.string().trim().min(1).max(128).regex(/^[A-Za-z0-9_-]+$/, "Select a valid subscription.");
var customerSubscriptionCancellationRequestSchema = z.object({
  action: z.enum(["cancel", "reactivate"])
});
var customerSubscriptionPlanRequestSchema = z.object({
  coupon: z.string().trim().min(1).max(256).nullable(),
  planId: z.string().trim().regex(/^(?:plan|price)_[A-Za-z0-9_]+$/, "Select a valid plan.")
});
var customerSubscriptionActionResponseSchema = z.object({
  action: z.enum(["add_on", "cancel", "change_plan", "reactivate"]),
  message: z.string().min(1),
  subscriptionId: z.string().min(1),
  success: z.literal(true)
});
var customerRewardRedemptionRequestSchema = z.object({
  rewardId: z.string().trim().min(1).max(128)
});
var customerRewardRedemptionResponseSchema = z.object({
  action: z.literal("redeem"),
  message: z.string().min(1),
  referralsRemaining: z.number().int().nonnegative(),
  rewardId: z.string().min(1),
  success: z.literal(true)
});
var customerPurchaseProductSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  features: z.array(z.string()),
  initialPaymentMinor: z.number().int().nonnegative(),
  interval: z.enum(["day", "week", "month", "year", "one_time"]),
  intervalCount: z.number().int().positive(),
  name: z.string().min(1),
  planId: z.string().min(1),
  priceMinor: z.number().int().nonnegative(),
  productId: z.string().min(1),
  stock: z.number().int().nonnegative(),
  trialDays: z.number().int().nonnegative()
});
var docusignEnvironmentUrlSchema = z.url().refine((value) => {
  const url = new URL(value);
  return url.protocol === "https:" && /^(?:[a-z0-9-]+\.)?docusign\.net$/i.test(url.hostname) && url.pathname === "/" && !url.search && !url.hash;
}, "Invalid DocuSign environment URL");
var customerPurchaseAgreementSchema = z.object({
  accountId: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/),
  clickwrapId: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/),
  environmentUrl: docusignEnvironmentUrlSchema
});
var customerPurchaseCatalogSchema = z.object({
  agreement: customerPurchaseAgreementSchema.nullable().default(null),
  analytics: trackingPixelsSchema.default({ google: "", meta: "", tiktok: "" }),
  billing: z.object({
    collectPhone: z.boolean(),
    paymentProvider: z.enum(["stripe", "unavailable"]),
    publishableKey: z.string().min(1).nullable(),
    requireAddress: z.boolean(),
    stripeAccountId: z.string().min(1).nullable(),
    taxPercentage: z.number().min(0).max(100)
  }),
  branding: z.object({
    backgroundUrl: safePublicAssetUrlSchema.nullable(),
    logoUrl: safePublicAssetUrlSchema.nullable(),
    name: z.string().min(1),
    primaryColor: hexColorSchema,
    purchaseMessage: z.string(),
    secondaryColor: hexColorSchema,
    termsUrl: z.url()
  }),
  conversionEvent: tiktokTrackingEventSchema.default(null),
  login: z.object({
    authorizationUrl: z.string().refine((value) => value.startsWith("/") || URL.canParse(value)).nullable(),
    required: z.boolean(),
    satisfied: z.boolean()
  }),
  products: z.array(customerPurchaseProductSchema).min(1)
});
var customerPurchaseCouponSchema = z.object({
  amountOffMinor: z.number().int().nonnegative().nullable(),
  code: z.string().min(1),
  duration: z.enum(["forever", "once", "repeating"]),
  durationMonths: z.number().int().positive().nullable(),
  percentOff: z.number().positive().max(100).nullable()
});
var customerPurchaseSetupIntentSchema = z.object({
  clientSecret: z.string().min(1),
  fixturePaymentMethodId: z.string().min(1).nullable()
});
var customerPurchaseRequestSchema = z.object({
  address: z.object({
    city: z.string().trim().min(1).max(120),
    country: z.string().trim().regex(/^[A-Z]{2}$/),
    line1: z.string().trim().min(1).max(240),
    line2: z.string().trim().max(240),
    postalCode: z.string().trim().min(1).max(32),
    state: z.string().trim().min(1).max(120)
  }).nullable(),
  agreedToTerms: z.literal(true),
  coupon: z.string().trim().min(1).max(128).nullable(),
  email: z.email(),
  firstName: z.string().trim().min(1).max(120),
  ignoreTrialEligibility: z.boolean(),
  lastName: z.string().trim().min(1).max(120),
  paymentMethodId: z.string().trim().regex(/^pm_[A-Za-z0-9_]+$/).nullable(),
  phone: z.string().trim().max(64),
  planId: z.string().min(1),
  slug: z.string().trim().min(1).max(256).nullable()
});
var customerPurchaseResponseSchema = z.object({
  clientSecret: z.string().min(1).nullable(),
  key: z.string().min(1),
  purchaseMessage: z.string(),
  redirect: z.string().refine((value) => value.startsWith("/") || URL.canParse(value)),
  requiresAction: z.boolean(),
  success: z.literal(true)
});
var adminStatusSchema = z.object({
  environment: z.enum(["development", "test", "production"]),
  service: z.literal("admin-api")
});
var adminRoleSchema = z.enum(["owner", "admin", "member"]);
var adminSessionSchema = z.object({
  account: z.object({
    email: z.email(),
    id: z.string().min(1),
    role: adminRoleSchema
  }),
  organization: z.object({
    groupLogo: safePublicAssetUrlSchema.nullable(),
    groupName: z.string().min(1),
    id: z.string().min(1),
    primaryColor: hexColorSchema,
    secondaryColor: hexColorSchema,
    tier: z.string().min(1)
  })
});
var adminLoginRequestSchema = z.object({
  email: z.email(),
  password: z.string().min(1)
});
var adminForgotPasswordRequestSchema = z.object({
  email: z.email()
});
var adminCredentialTokenSchema = z.string().min(8).max(128).regex(/^[A-Za-z0-9_-]+$/);
var adminPasswordActionRequestSchema = z.object({
  confirmPassword: z.string(),
  password: z.string().min(8).max(128).regex(/[0-9]/, "Password must contain a number.").regex(/[^A-Za-z0-9]/, "Password must contain a special character.")
}).refine((value) => value.password === value.confirmPassword, {
  message: "Passwords must match.",
  path: ["confirmPassword"]
});
var adminAuthTokenStatusSchema = z.object({
  valid: z.boolean()
});
var adminAuthActionResponseSchema = z.object({
  message: z.string().min(1),
  success: z.literal(true)
});
var adminLoginResponseSchema = z.object({
  session: adminSessionSchema,
  success: z.literal(true)
});
var adminSessionResponseSchema = z.discriminatedUnion("authenticated", [
  z.object({
    authenticated: z.literal(true),
    /**
     * Member tracking (COMMUNITY_FEATURES members): the business tracks who is in its server, so
     * its Customers page lists members by default from the first request. Absent otherwise.
     */
    memberTracking: z.literal(true).optional(),
    session: adminSessionSchema
  }),
  z.object({
    authenticated: z.literal(false)
  })
]);
var adminRegistrationAuthorizeSchema = z.object({
  url: z.string().refine((value) => value.startsWith("/") || URL.canParse(value), "Enter a safe redirect URL.")
});
var adminRegistrationIdentitySchema = z.object({
  avatarUrl: safePublicAssetUrlSchema.nullable(),
  /**
   * Null when Discord gave no email (G-8): the owner adds one and confirms it with a code
   * (`adminRegistrationEmailCodeRequestSchema`) before the account step.
   */
  email: z.email().nullable(),
  username: z.string().min(1)
});
var adminRegistrationGuildSchema = z.object({
  iconUrl: safePublicAssetUrlSchema,
  id: z.string().min(1),
  name: z.string().min(1)
});
var adminRegistrationPlanSchema = z.object({
  amountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** The per-sale platform fee a business on this plan pays; 0 on a free plan. */
  feePercent: z.number().min(0).max(30),
  interval: z.enum(["day", "week", "month", "year"]),
  /** Billed every this many intervals; 1 when left out. */
  intervalCount: z.number().int().positive().optional(),
  priceId: z.string().regex(/^price_[A-Za-z0-9]+$/),
  tier: z.enum(["free", "starter", "pro", "enterprise"])
});
var adminRegistrationContextSchema = z.object({
  billing: z.object({
    amountMinor: z.number().int().positive(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** The registration price's own interval (G-6). */
    interval: z.enum(["day", "week", "month", "year"]),
    /** Billed every this many intervals (Stripe's interval_count); 1 when left out. */
    intervalCount: z.number().int().positive().optional(),
    /**
     * The plans to choose from, in PLATFORM_PRICE_TIERS's order, when it lists two or more; absent
     * otherwise, and registration charges the one configured price as before.
     */
    plans: z.array(adminRegistrationPlanSchema).optional(),
    publishableKey: z.string().min(1).nullable()
  }),
  contextToken: z.string().min(32),
  guild: adminRegistrationGuildSchema,
  user: adminRegistrationIdentitySchema
});
var adminRegistrationEmailCodeRequestSchema = z.object({
  contextToken: z.string().min(32),
  email: z.email().max(254)
});
var adminRegistrationEmailCodeSchema = z.object({
  codeToken: z.string().min(32),
  expiresInMinutes: z.number().int().positive()
});
var adminRegistrationEmailVerifyRequestSchema = z.object({
  code: z.string().regex(/^[0-9]{6}$/),
  codeToken: z.string().min(32),
  contextToken: z.string().min(32)
});
var adminRegistrationCouponSchema = z.object({
  amountOffMinor: z.number().int().positive().nullable(),
  code: z.string().min(1),
  duration: z.enum(["forever", "once", "repeating"]),
  percentOff: z.number().positive().max(100).nullable()
});
var adminRegistrationSetupIntentRequestSchema = z.object({
  contextToken: z.string().min(32)
});
var adminRegistrationSetupIntentSchema = z.object({
  clientSecret: z.string().min(1),
  fixturePaymentMethodId: z.string().min(1).nullable()
});
var adminRegistrationRequestSchema = adminPasswordActionRequestSchema.extend({
  contextToken: z.string().min(32),
  coupon: z.string().trim().min(1).max(128).nullable(),
  /** The card, for a paid plan. A free plan takes none. */
  paymentMethodId: z.string().trim().regex(/^pm_[A-Za-z0-9_]+$/).optional(),
  /** The plan chosen from the context's `plans` (P2). Left out, the one configured price. */
  priceId: z.string().regex(/^price_[A-Za-z0-9]+$/).optional()
});
var adminRegistrationResponseSchema = z.object({
  /**
   * The admin SPA page that finishes the steps registration did not cover (ruling R27). It holds
   * the onboarding link's raw token, which no other response repeats. Optional, so a response
   * from before the hand-off still parses.
   */
  onboardingUrl: z.string().regex(/^\/onboarding\/[A-Za-z0-9_-]{8,128}$/).optional(),
  /**
   * P4: the first payment waits on the bank's check (3D Secure). Stripe.js finishes it with this
   * client secret, then POST /api/v1/admin/account/confirm-payment activates the business.
   */
  paymentAction: z.object({ clientSecret: z.string().min(1) }).optional(),
  session: adminSessionSchema,
  success: z.literal(true)
});
var adminOnboardingTokenSchema = adminCredentialTokenSchema;
var adminOnboardingStageSchema = z.enum(["discord", "branding", "account", "payment"]);
var adminOnboardingTrackingStatusSchema = z.enum(["pending", "complete", "skipped"]);
var onboardingStepStatusSchema = z.enum(["done", "pending"]);
var adminLegalStatusSchema = z.object({ ready: z.boolean(), standardAvailable: z.boolean() }).default({ ready: false, standardAvailable: false });
var adminOnboardingCatalogSchema = z.object({
  /**
   * The business's live automatic address, once registration made one under the platform's base
   * domain; `null` without one. Defaults to `null`, so earlier catalogs still parse.
   */
  address: z.object({ url: httpUrlSchema }).nullable().default(null),
  billing: z.object({
    amountMinor: z.number().int().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    publishableKey: z.string().min(1).nullable()
  }),
  branding: z.object({
    contactUrl: z.string(),
    groupName: z.string(),
    primaryColor: hexColorSchema,
    privacyUrl: z.string().default(""),
    secondaryColor: hexColorSchema,
    standardLegal: z.boolean().default(false),
    termsUrl: z.string()
  }),
  discord: z.object({
    authorizationUrl: z.string().refine((value) => value.startsWith("/") || URL.canParse(value)),
    serverId: z.string()
  }),
  legal: adminLegalStatusSchema,
  organizationId: z.string().min(1),
  payment: z.object({
    connectAvailable: z.boolean(),
    connected: z.boolean(),
    provider: z.literal("stripe")
  }),
  pixels: trackingPixelsSchema.default({ google: "", meta: "", tiktok: "" }),
  stage: adminOnboardingStageSchema,
  /**
   * Each stage, done or pending (ruling R27). `stage` is the first one still pending, or
   * `payment`. A self-registered tenant starts with `discord` and `account` (the owner and its
   * platform billing) done. `payment` is the payouts step: done once Stripe is connected, and
   * skippable, because verify no longer waits on it (R28). Tracking reports its own status in
   * `tracking.status`. Optional, like `payment` inside it, so earlier catalogs still parse.
   */
  steps: z.object({
    account: onboardingStepStatusSchema,
    branding: onboardingStepStatusSchema,
    discord: onboardingStepStatusSchema,
    payment: onboardingStepStatusSchema.optional()
  }).optional(),
  tracking: z.object({ status: adminOnboardingTrackingStatusSchema }).default({ status: "pending" })
});
var adminOnboardingDiscordRequestSchema = z.object({
  serverId: z.string().trim().min(3).max(64).regex(/^[A-Za-z0-9_-]+$/)
});
var adminOnboardingBrandingRequestSchema = z.object({
  contactUrl: z.url(),
  groupName: z.string().trim().min(1).max(120),
  primaryColor: hexColorSchema,
  privacyUrl: httpUrlSchema.optional(),
  secondaryColor: hexColorSchema,
  // Ternary's standard terms (G5); admin-api refuses true while they are not available.
  standardLegal: z.boolean().optional(),
  termsUrl: z.url().optional()
}).refine((input) => input.termsUrl !== void 0 || input.standardLegal === true, {
  message: "Enter your terms page.",
  path: ["termsUrl"]
});
var adminOnboardingPixelsRequestSchema = trackingPixelsSchema;
var adminOnboardingAccountRequestSchema = adminPasswordActionRequestSchema.extend({
  email: z.email(),
  paymentMethodId: z.string().trim().regex(/^pm_[A-Za-z0-9_]+$/)
});
var adminOnboardingConnectSchema = z.object({
  url: z.url()
});
var adminOnboardingCompleteSchema = z.object({
  complete: z.literal(true),
  success: z.literal(true)
});
var adminOnboardingLinkSchema = z.object({
  onboardingUrl: z.string().regex(/^\/onboarding\/[A-Za-z0-9_-]{8,128}$/)
});
var adminSetupItemIdSchema = z.enum([
  "discord",
  "brand",
  "legal",
  "tracking",
  "product",
  "member"
]);
var adminSetupSchema = z.object({
  items: z.array(
    z.object({
      done: z.boolean(),
      href: z.string().regex(/^\/dashboard\//),
      id: adminSetupItemIdSchema,
      optional: z.boolean()
    })
  ),
  onboardingOpen: z.boolean()
});
var adminAlertSchema = z.object({
  id: z.string().trim().min(1).max(128),
  kind: z.enum(["notice", "update", "warning"]),
  message: z.string().trim().min(1).max(500)
});
var adminAlertCatalogSchema = z.object({
  alerts: z.array(adminAlertSchema).max(50)
});
var adminProductIntervalSchema = z.enum(["day", "week", "month", "year", "one_time"]);
var adminProductRemovalSchema = z.enum(["kick", "roles"]);
var adminProductTypeSchema = z.enum(["discord", "manual"]);
var adminProductSchema = z.object({
  additionalPriceIds: z.array(z.string().min(1)).default([]),
  addOns: z.array(z.string().min(1)),
  currency: z.string().regex(/^[A-Z]{3}$/),
  features: z.array(z.string().min(1)),
  id: z.string().min(1),
  initialPaymentMinor: z.number().int().nonnegative(),
  initialPaymentPriceId: z.string().min(1).nullable().default(null),
  interval: adminProductIntervalSchema,
  intervalCount: z.number().int().positive(),
  name: z.string().min(1),
  planId: z.string().min(1),
  priceMinor: z.number().int().nonnegative(),
  productId: z.string().min(1),
  productType: adminProductTypeSchema,
  removal: adminProductRemovalSchema,
  roleRemoval: z.array(z.string().min(1)),
  roles: z.array(z.string().min(1)),
  slug: z.string().min(1).nullable(),
  stock: z.number().int().nonnegative(),
  trialDays: z.number().int().min(0).max(730),
  trialRoles: z.array(z.string().min(1)),
  upgradeTo: z.array(z.string().min(1))
});
var uniqueValues = (values) => new Set(values).size === values.length;
var signupFieldKeySchema = z.string().regex(
  /^[a-z][a-z0-9_]{1,31}$/,
  "Use 2-32 lowercase letters, digits or underscores, starting with a letter."
);
var signupFieldKindSchema = z.enum([
  "phone",
  "country",
  "text",
  "select",
  "multiselect",
  "checkbox",
  "date"
]);
var signupFieldSchema = z.object({
  key: signupFieldKeySchema,
  kind: signupFieldKindSchema,
  label: z.string().trim().min(1).max(80),
  options: z.array(z.string().trim().min(1).max(60)).min(1).max(30).refine(uniqueValues, "Each option must be different.").optional(),
  required: z.boolean(),
  step: z.literal(1)
}).superRefine((field, context) => {
  const choice = field.kind === "select" || field.kind === "multiselect";
  if (choice !== (field.options !== void 0)) {
    context.addIssue({
      code: "custom",
      message: choice ? "Add between 1 and 30 options." : "Only select fields have options.",
      path: ["options"]
    });
  }
  if ((field.key === "phone" || field.key === "country") && field.kind !== field.key) {
    context.addIssue({
      code: "custom",
      message: `The ${field.key} field must use the ${field.key} type.`,
      path: ["kind"]
    });
  }
});
var reservedSignupFieldKeys = /* @__PURE__ */ new Set(["firstname", "lastname", "email"]);
var signupFieldsSchema = z.array(signupFieldSchema).max(12, "A signup form has at most 12 fields.").superRefine((fields, context) => {
  const keys = /* @__PURE__ */ new Set();
  for (const [index, field] of fields.entries()) {
    if (reservedSignupFieldKeys.has(field.key)) {
      context.addIssue({
        code: "custom",
        message: "First name, last name and email are always asked.",
        path: [index, "key"]
      });
    } else if (keys.has(field.key)) {
      context.addIssue({
        code: "custom",
        message: "Each field needs a different key.",
        path: [index, "key"]
      });
    }
    keys.add(field.key);
  }
});
var defaultFreeProductSignupFields = [
  { key: "phone", kind: "phone", label: "Phone number", required: false, step: 1 },
  { key: "country", kind: "country", label: "Country", required: true, step: 1 },
  {
    key: "heard_about",
    kind: "select",
    label: "How did you hear about us?",
    options: [
      "Meta/Instagram",
      "TikTok",
      "Google/YouTube",
      "X/Twitter",
      "Discord",
      "Friend or referral",
      "Other"
    ],
    required: true,
    step: 1
  }
];
var productAccessSchema = z.object({
  guildId: discordSnowflakeSchema,
  roleIds: z.array(discordSnowflakeSchema).max(50).refine(uniqueValues, "Select each role once.")
});
var signupSlugSchema = z.string().regex(/^[a-z0-9-]{3,48}$/, "Use 3-48 lowercase letters, digits or hyphens.");
var customerTenantSchema = customerContextSchema.extend({
  signupSlug: signupSlugSchema.nullable().default(null)
});
var productImageUrlSchema = z.string().regex(/^\/api\/v1\/product-images\/[a-f0-9]{24}\/[A-Za-z0-9_-]{22}$/);
var adminCreateFreeProductRequestSchema = z.strictObject({
  access: productAccessSchema,
  description: z.string().trim().max(2e3).default(""),
  imageUrl: productImageUrlSchema.nullable().default(null),
  name: z.string().trim().min(1).max(120),
  pricing: z.strictObject({ type: z.literal("free") }),
  signupFields: signupFieldsSchema.default(
    () => structuredClone([...defaultFreeProductSignupFields])
  ),
  slug: signupSlugSchema
});
var adminUpdateFreeProductRequestSchema = adminCreateFreeProductRequestSchema;
var adminFreeProductSchema = z.object({
  access: productAccessSchema,
  createdAt: z.iso.datetime(),
  description: z.string().max(2e3),
  id: objectIdSchema,
  imageUrl: productImageUrlSchema.nullable(),
  name: z.string().min(1).max(120),
  pricing: z.object({ type: z.literal("free") }),
  signupFields: signupFieldsSchema,
  signupPath: z.string(),
  slug: signupSlugSchema,
  updatedAt: z.iso.datetime()
}).refine((product) => product.signupPath === `/signup/${product.slug}`, {
  message: "The signup path must match the slug.",
  path: ["signupPath"]
});
var reservedTenantHandles = [
  "account",
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "billing",
  "blog",
  "cdn",
  "checkout",
  "customer",
  "dashboard",
  "dashboardinfo",
  "dev",
  "docs",
  // CUSTOM_DOMAIN_TARGET is domains.<base>, which customers' domains CNAME to.
  "domains",
  "feed",
  "help",
  "home",
  "join",
  "legal",
  "link-expired",
  "login",
  "logout",
  "mail",
  "monitoring",
  "order",
  "orderinfo",
  "orders",
  "out-of-stock",
  "platform",
  "preview",
  "privacy",
  "registerinfo",
  "signup",
  "staging",
  "static",
  "status",
  "support",
  "ternary",
  "test",
  "tos",
  "www"
];
var tenantHandleFormatMessage = "Use 3 to 40 lowercase letters, numbers or hyphens, starting and ending with a letter or number.";
var tenantHandleSchema = z.string().trim().regex(/^(?!.*--)[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/i, tenantHandleFormatMessage).toLowerCase().refine((handle) => !reservedTenantHandles.includes(handle), "That address is reserved.");
var adminAddressSchema = z.object({
  baseDomain: z.string().min(1),
  changeable: z.boolean(),
  handle: z.string().min(1),
  hostname: z.string().min(1),
  /** Earlier handles, which keep redirecting to this address until `redirectUntil`. */
  retired: z.array(z.object({ hostname: z.string().min(1), redirectUntil: z.iso.datetime() })),
  url: httpUrlSchema
});
var adminAddressRequestSchema = z.object({ handle: tenantHandleSchema });
var adminAddressCheckSchema = z.object({
  available: z.boolean(),
  reason: z.enum(["current", "invalid", "reserved", "taken"]).optional()
});
var discordPermission = {
  administrator: 0x8n,
  changeNickname: 0x4000000n,
  createInstantInvite: 0x1n,
  kickMembers: 0x2n,
  manageRoles: 0x10000000n,
  readMessageHistory: 0x10000n,
  sendMessages: 0x800n,
  /** Past JavaScript's 32-bit `|`, which is why every bit here is a BigInt. */
  sendPolls: 1n << 49n,
  viewChannel: 0x400n
};
function olderDiscordId(a, b) {
  return a.length !== b.length ? a.length < b.length : a < b;
}
var everyDiscordPermission = (1n << 64n) - 1n;
function discordBotReach(input) {
  const botRoles = new Set(input.botRoleIds);
  let permissions = 0n;
  let topPosition = 0;
  let topRoleId;
  for (const role of input.roles) {
    const everyone = role.id === input.guildId;
    if (!everyone && !botRoles.has(role.id)) {
      continue;
    }
    permissions |= /^\d{1,20}$/.test(role.permissions) ? BigInt(role.permissions) : 0n;
    if (!everyone && (topRoleId === void 0 || role.position > topPosition || role.position === topPosition && olderDiscordId(role.id, topRoleId))) {
      topPosition = role.position;
      topRoleId = role.id;
    }
  }
  return {
    permissions: permissions & discordPermission.administrator ? everyDiscordPermission : permissions,
    topPosition,
    ...topRoleId === void 0 ? {} : { topRoleId }
  };
}
var pollChannelPermissions = discordPermission.viewChannel | discordPermission.sendMessages | discordPermission.sendPolls | discordPermission.readMessageHistory;
function discordChannelPermissions(reach, channel, botRoleIds, botUserId, guildId) {
  if ((reach.permissions & discordPermission.administrator) !== 0n) {
    return everyDiscordPermission;
  }
  const bits = (value) => /^\d{1,20}$/.test(value) ? BigInt(value) : 0n;
  const overwrites = channel.permission_overwrites;
  let permissions = reach.permissions;
  const everyone = overwrites.find(({ id, type }) => type === 0 && id === guildId);
  if (everyone) {
    permissions = permissions & ~bits(everyone.deny) | bits(everyone.allow);
  }
  const roles = new Set(botRoleIds);
  let allow = 0n;
  let deny = 0n;
  for (const overwrite of overwrites) {
    if (overwrite.type === 0 && overwrite.id !== guildId && roles.has(overwrite.id)) {
      allow |= bits(overwrite.allow);
      deny |= bits(overwrite.deny);
    }
  }
  permissions = permissions & ~deny | allow;
  const own = overwrites.find(({ id, type }) => type === 1 && id === botUserId);
  if (own) {
    permissions = permissions & ~bits(own.deny) | bits(own.allow);
  }
  return permissions;
}
function canAssignDiscordRole(reach, role) {
  const manages = (reach.permissions & (discordPermission.manageRoles | discordPermission.administrator)) !== 0n;
  const below = role.position < reach.topPosition || role.position === reach.topPosition && role.id !== void 0 && reach.topRoleId !== void 0 && olderDiscordId(reach.topRoleId, role.id);
  return manages && below;
}
var adminBotHealthSchema = z.object({
  /** Community data only (absent while every COMMUNITY_FEATURES value is off): bot profile. */
  changeNickname: z.boolean().optional(),
  createInvite: z.boolean(),
  /**
   * Kick Members, which a lapse needs when a paid product removes the member from the server.
   * Present only for the server where a paid product does that; absent everywhere else, and from
   * answers that predate it.
   */
  kickMembers: z.boolean().optional(),
  manageRoles: z.boolean(),
  /** Community data only: polls and saved posts read messages back. */
  readMessageHistory: z.boolean().optional(),
  sendMessages: z.boolean(),
  /** Community data only: posting a poll. */
  sendPolls: z.boolean().optional(),
  viewChannels: z.boolean()
});
var adminDiscordRoleSchema = z.object({
  /** False when the bot cannot give the role; absent from catalogs that predate the flag. */
  assignable: z.boolean().default(true),
  id: z.string().min(1),
  name: z.string().min(1)
});
var adminDiscordGuildSchema = z.object({
  /** The bot's health in the guild; null when Discord could not say, absent when not read. */
  bot: adminBotHealthSchema.nullable().optional(),
  /** False when the bot cannot reach the guild, so re-adding it is the fix; absent when not read. */
  connected: z.boolean().optional(),
  id: discordSnowflakeSchema,
  name: z.string(),
  primary: z.boolean(),
  /** The admin path that re-adds the bot to this guild with the guild preselected. */
  reinstallUrl: z.string().optional()
});
var adminCreateDiscordRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(100)
});
var adminProductGuildSchema = adminDiscordGuildSchema.extend({
  /**
   * The name of the bot's highest role, which the owner drags above a role the bot cannot give.
   * Absent when Discord could not say. It is not always "Ternary": a business may run its own app.
   */
  botRole: z.string().min(1).optional(),
  /** False when the bot cannot reach the guild; absent from catalogs that predate the flag. */
  connected: z.boolean().optional(),
  roles: z.array(adminDiscordRoleSchema)
});
var adminProductCatalogSchema = z.object({
  freeProducts: z.array(adminFreeProductSchema).default([]),
  freeWritable: z.boolean().default(false),
  guilds: z.array(adminProductGuildSchema).default([]),
  /** Whether a free product's signup page can publish (G5), so admin-web never recomputes it. */
  legal: adminLegalStatusSchema,
  mode: z.literal("live"),
  products: z.array(adminProductSchema),
  providerProducts: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1)
    })
  ).default([]),
  provider: z.literal("stripe"),
  roles: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1)
    })
  ),
  storefrontUrl: z.url(),
  writable: z.boolean()
});
var adminProductFieldsSchema = z.object({
  addOns: z.array(z.string().min(1)).default([]),
  currency: z.string().regex(/^[A-Z]{3}$/),
  features: z.array(z.string().trim().min(1)).default([]),
  initialPaymentMinor: z.number().int().nonnegative().default(0),
  interval: adminProductIntervalSchema,
  intervalCount: z.number().int().positive(),
  name: z.string().trim().min(1).max(120),
  priceMinor: z.number().int().nonnegative(),
  productType: adminProductTypeSchema,
  removal: adminProductRemovalSchema,
  roleRemoval: z.array(z.string().min(1)).default([]),
  roles: z.array(z.string().min(1)).default([]),
  slug: z.string().trim().regex(/^[a-zA-Z0-9_-]+$/).max(120).nullable(),
  stock: z.number().int().nonnegative(),
  trialDays: z.number().int().min(0).max(730).default(0),
  trialRoles: z.array(z.string().min(1)).default([]),
  upgradeTo: z.array(z.string().min(1)).default([])
}).superRefine((product, context) => {
  if (product.productType === "discord" && product.roles.length === 0) {
    context.addIssue({
      code: "custom",
      message: "Select at least one Discord role.",
      path: ["roles"]
    });
  }
  if (product.removal === "roles" && product.roleRemoval.length === 0) {
    context.addIssue({
      code: "custom",
      message: "Select at least one role to remove.",
      path: ["roleRemoval"]
    });
  }
  if (product.interval === "one_time" && (product.trialDays > 0 || product.initialPaymentMinor > 0)) {
    context.addIssue({
      code: "custom",
      message: "One-time products cannot have a trial or initial payment.",
      path: ["interval"]
    });
  }
});
var adminCreateProductRequestSchema = adminProductFieldsSchema.extend({
  existingProductId: z.string().trim().min(1).max(128).nullable().default(null)
});
var adminUpdateProductRequestSchema = adminProductFieldsSchema;
var adminProductRequestSchema = z.union([
  adminCreateFreeProductRequestSchema,
  adminCreateProductRequestSchema
]);
var adminProductImageSchema = z.object({
  url: productImageUrlSchema
});
var adminDeleteProductsRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100)
});
var nullableDateTimeSchema = z.iso.datetime().nullable();
var adminMemberSubscriptionSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/).nullable(),
  currentPeriodStart: nullableDateTimeSchema,
  id: z.string().min(1),
  interval: adminProductIntervalSchema.nullable(),
  intervalCount: z.number().int().positive().nullable(),
  nextInvoiceAt: nullableDateTimeSchema,
  planId: z.string().min(1),
  priceMinor: z.number().int().nonnegative().nullable(),
  roles: z.array(z.string().min(1)),
  startedAt: nullableDateTimeSchema,
  status: z.enum(["active", "cancelling", "canceled", "unknown"]),
  trialEndsAt: nullableDateTimeSchema,
  type: z.enum(["recurring", "lifetime", "manual"])
});
var adminMemberSchema = z.object({
  address: z.object({
    city: z.string(),
    country: z.string(),
    line1: z.string(),
    line2: z.string(),
    phone: z.string(),
    postalCode: z.string(),
    state: z.string()
  }).nullable(),
  customerEmail: z.email(),
  customerId: z.string().min(1),
  discord: z.object({
    avatarUrl: z.url().nullable(),
    id: z.string().nullable(),
    status: z.enum(["connected", "unlinked", "unavailable"]),
    username: z.string().nullable()
  }),
  firstName: z.string().nullable(),
  id: z.string().min(1),
  key: z.string().min(1),
  lastName: z.string().nullable(),
  referralCode: z.string().min(1),
  subscriptions: z.array(adminMemberSubscriptionSchema),
  totalReferrals: z.number().int().nonnegative()
});
var adminMemberCatalogSchema = z.object({
  members: z.array(adminMemberSchema),
  mode: z.literal("live"),
  products: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1),
      planId: z.string().min(1)
    })
  ),
  roles: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1)
    })
  ),
  writable: z.boolean()
});
var adminMemberDetailSchema = z.object({
  invoices: z.array(
    z.object({
      amountDueMinor: z.number().int().nonnegative(),
      createdAt: z.iso.datetime(),
      currency: z.string().regex(/^[A-Z]{3}$/),
      dueAt: nullableDateTimeSchema,
      hostedUrl: z.url().nullable(),
      id: z.string().min(1),
      number: z.string().min(1),
      paid: z.boolean(),
      pdfUrl: z.url().nullable()
    })
  ),
  member: adminMemberSchema,
  payments: z.array(
    z.object({
      amountReceivedMinor: z.number().int().nonnegative(),
      createdAt: z.iso.datetime(),
      currency: z.string().regex(/^[A-Z]{3}$/),
      description: z.string(),
      id: z.string().min(1),
      status: z.string().min(1)
    })
  )
});
var adminCreateMemberRequestSchema = z.object({
  customerEmail: z.email(),
  customerId: z.string().trim().max(120).regex(/^cus_[A-Za-z0-9]+$/),
  discordId: z.string().trim().min(1).max(40).nullable(),
  key: z.string().trim().min(1).max(160).transform((key) => key.replaceAll(" ", "")),
  planId: z.string().trim().min(1),
  roles: z.array(z.string().min(1)).min(1),
  subscriptionId: z.string().trim().min(1).max(160),
  subscriptionType: z.enum(["recurring", "lifetime", "manual"])
});
var adminUpdateMemberRequestSchema = z.object({
  customerEmail: z.email(),
  customerId: z.string().trim().max(120).regex(/^cus_[A-Za-z0-9]+$/),
  discordId: z.string().trim().min(1).max(40).nullable(),
  totalReferrals: z.number().int().nonnegative()
});
var adminDeleteMembersRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100)
});
var adminDiscordMemberSchema = z.object({
  avatarUrl: z.url().nullable(),
  id: z.string().min(1),
  username: z.string().min(1)
});
var adminCouponSchema = z.object({
  amountOffMinor: z.number().int().positive().nullable(),
  appliesToProductIds: z.array(z.string().min(1)),
  currency: z.string().regex(/^[A-Z]{3}$/),
  duration: z.enum(["forever", "once", "repeating"]),
  durationMonths: z.number().int().positive().nullable(),
  id: z.string().min(1),
  maxRedemptions: z.number().int().positive().nullable(),
  name: z.string().min(1),
  percentOff: z.number().positive().max(100).nullable(),
  redeemBy: z.iso.datetime().nullable(),
  redemptions: z.number().int().nonnegative(),
  type: z.enum(["percent", "fixed"])
});
var adminCouponCatalogSchema = z.object({
  coupons: z.array(adminCouponSchema),
  mode: z.literal("live"),
  products: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1),
      productId: z.string().min(1),
      stock: z.number().int().nonnegative()
    })
  ),
  provider: z.literal("stripe"),
  writable: z.boolean()
});
var adminCreateCouponRequestSchema = z.object({
  amountOffMinor: z.number().int().positive().nullable(),
  appliesToProductIds: z.array(z.string().min(1)).default([]),
  code: z.string().trim().regex(/^[a-zA-Z0-9_-]+$/).max(40).nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  duration: z.enum(["forever", "once", "repeating"]),
  durationMonths: z.number().int().positive().max(36).nullable(),
  maxRedemptions: z.number().int().positive().nullable(),
  name: z.string().trim().min(1).max(120),
  percentOff: z.number().positive().max(100).nullable(),
  redeemBy: z.iso.datetime().nullable(),
  type: z.enum(["percent", "fixed"])
}).superRefine((coupon, context) => {
  if (coupon.type === "percent" && coupon.percentOff === null) {
    context.addIssue({
      code: "custom",
      message: "Enter a percentage discount.",
      path: ["percentOff"]
    });
  }
  if (coupon.type === "fixed" && coupon.amountOffMinor === null) {
    context.addIssue({
      code: "custom",
      message: "Enter a fixed discount.",
      path: ["amountOffMinor"]
    });
  }
  if (coupon.duration === "repeating" && coupon.durationMonths === null) {
    context.addIssue({
      code: "custom",
      message: "Enter the number of discount months.",
      path: ["durationMonths"]
    });
  }
});
var adminRewardSchema = z.object({
  creditAmountMinor: z.number().int().nonnegative().nullable(),
  id: z.string().min(1),
  name: z.string().min(1),
  referralCost: z.number().int().positive()
});
var adminRewardCatalogSchema = z.object({
  mode: z.literal("live"),
  provider: z.literal("stripe"),
  rewards: z.array(adminRewardSchema),
  writable: z.boolean()
});
var adminCreateRewardRequestSchema = z.object({
  creditAmountMinor: z.number().int().nonnegative().nullable(),
  name: z.string().trim().min(1).max(120),
  referralCost: z.number().int().positive().max(1e6)
});
var adminDiscordReactionRoleSchema = z.object({
  emoji: z.string().trim().min(1).max(32),
  roleId: z.string().trim().min(1).max(64)
});
var adminMemberTrackingSchema = z.object({
  /** The oldest last full check of every tracked server; null until each has one. */
  checkedAt: z.iso.datetime().nullable(),
  guilds: z.array(
    z.object({
      id: discordSnowflakeSchema,
      state: z.enum(["ok", "bot_gone", "checking"])
    })
  ),
  /** `probe` while a server's members are checked one by one (no Server Members Intent). */
  mode: z.enum(["list", "probe"]).nullable(),
  tracking: z.boolean()
});
var adminMemberTrackingRequestSchema = z.object({ tracking: z.boolean() });
var pollLimits = {
  answer: 55,
  maxAnswers: 10,
  maxHours: 768,
  minAnswers: 2,
  question: 300
};
var characters = (max) => z.string().trim().min(1).refine((value) => Array.from(value).length <= max);
var unicodeEmojiPattern = /^\p{Extended_Pictographic}\uFE0F?\p{Emoji_Modifier}?(?:\u200D\p{Extended_Pictographic}\uFE0F?\p{Emoji_Modifier}?)*$/u;
var keycapEmojiPattern = /^[0-9#*]️?⃣$/u;
var flagRegions = new Set(
  `AC AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR
  BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CP CQ CR CU CV CW CX CY CZ DE DG DJ DK DM DO
  DZ EA EC EE EG EH ER ES ET EU FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU
  GW GY HK HM HN HR HT HU IC ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY
  KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX
  MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS
  RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TA TC TD TF TG TH TJ TK TL TM
  TN TO TR TT TV TW TZ UA UG UM UN US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW`.split(
    /\s+/
  )
);
var regionalIndicatorA = 127462;
var isFlagEmoji = (value) => {
  const points = Array.from(value, (character) => character.codePointAt(0) ?? 0);
  return points.length === 2 && points.every((point) => point >= regionalIndicatorA && point < regionalIndicatorA + 26) && flagRegions.has(String.fromCharCode(...points.map((point) => point - regionalIndicatorA + 65)));
};
var customEmojiPattern = /^<a?:([A-Za-z0-9_]{2,32}):(\d{15,22})>$/;
function pollEmoji(value) {
  const custom = customEmojiPattern.exec(value);
  if (custom) {
    return { id: custom[2] ?? null, name: custom[1] ?? "" };
  }
  return unicodeEmojiPattern.test(value) || keycapEmojiPattern.test(value) || isFlagEmoji(value) ? { id: null, name: value } : null;
}
var savedChannelsMax = 20;
var savedChannelMissingSchema = z.array(z.enum(["viewChannel", "readMessageHistory"]));
var adminSavedChannelOptionSchema = z.object({
  botCanRead: z.boolean(),
  channelId: discordSnowflakeSchema,
  guildId: discordSnowflakeSchema,
  guildName: z.string(),
  kind: z.enum(["text", "announcement", "forum"]),
  missing: savedChannelMissingSchema,
  name: z.string(),
  /** Of `missing`, what the bot lacks server-wide too (the rest is the channel's overwrites). */
  serverMissing: savedChannelMissingSchema.optional(),
  /** Re-adds the bot to the server asking for what it lacks there; only with `serverMissing`. */
  reinstallUrl: z.string().optional()
});
var adminSavedChannelsSchema = z.object({
  /** Every text, announcement and forum channel of the business's servers. */
  available: z.array(adminSavedChannelOptionSchema),
  channels: z.array(
    adminSavedChannelOptionSchema.extend({
      addedAt: z.iso.datetime(),
      /** Its server no longer lists it while the bot is there: deleted in Discord. */
      deleted: z.literal(true).optional(),
      includeReplies: z.boolean()
    })
  ),
  /** False while Discord withholds message text: posts are saved without it until then. */
  contentAvailable: z.boolean(),
  /** Owners and admins choose the channels; anyone else only reads. */
  writable: z.boolean()
});
var adminSavedChannelsRequestSchema = z.object({
  channels: z.array(
    z.object({
      channelId: discordSnowflakeSchema,
      guildId: discordSnowflakeSchema,
      includeReplies: z.boolean().default(true)
    })
  ).max(savedChannelsMax).refine(
    (channels) => new Set(channels.map(({ channelId }) => channelId)).size === channels.length
  )
});
var optionalDate = z.union([z.iso.date(), z.iso.datetime()]).optional();
var adminSavedPostListQuerySchema = z.object({
  channelId: discordSnowflakeSchema.optional(),
  cursor: z.string().max(200).optional(),
  from: optionalDate,
  search: z.string().trim().min(1).max(100).optional(),
  sort: z.enum(["time", "channel"]).default("time"),
  to: optionalDate
});
var adminSavedPostSchema = z.object({
  /** Each file, with its download address once a copy is stored. */
  attachments: z.array(
    z.object({
      contentType: z.string().nullable(),
      downloadUrl: z.string().nullable(),
      filename: z.string(),
      id: discordSnowflakeSchema,
      size: z.number().int().min(0)
    })
  ),
  authorId: discordSnowflakeSchema,
  authorUsername: z.string(),
  channelId: discordSnowflakeSchema,
  /** The channel's name when it was chosen. */
  channelName: z.string(),
  content: z.string(),
  contentAvailable: z.boolean(),
  deletedAt: z.iso.datetime().nullable(),
  editedAt: z.iso.datetime().nullable(),
  guildId: discordSnowflakeSchema,
  id: z.string().min(1),
  messageId: discordSnowflakeSchema,
  parentMessageId: discordSnowflakeSchema.nullable(),
  postedAt: z.iso.datetime(),
  threadId: discordSnowflakeSchema.nullable(),
  threadName: z.string().nullable()
});
var adminSavedPostListSchema = z.object({
  contentAvailable: z.boolean(),
  data: z.array(adminSavedPostSchema),
  nextCursor: z.string().nullable()
});
var adminSavedPostDetailSchema = z.object({
  post: adminSavedPostSchema,
  replies: z.array(adminSavedPostSchema)
});
var adminPollCreateRequestSchema = z.object({
  allowMultiselect: z.boolean().default(false),
  answers: z.array(
    z.object({
      /** One emoji shown before the answer (`pollEmoji`), or none. */
      emoji: z.string().max(64).refine((value) => pollEmoji(value) !== null).nullable().default(null),
      text: characters(pollLimits.answer)
    })
  ).min(pollLimits.minAnswers).max(pollLimits.maxAnswers),
  channelId: discordSnowflakeSchema,
  durationHours: z.number().int().min(1).max(pollLimits.maxHours),
  guildId: discordSnowflakeSchema,
  question: characters(pollLimits.question)
});
var adminPollSchema = z.object({
  allowMultiselect: z.boolean(),
  /** Each answer with its votes at the last read. */
  answers: z.array(
    z.object({
      emoji: z.string().nullable(),
      id: z.number().int(),
      text: z.string(),
      votes: z.number().int().min(0)
    })
  ),
  channelId: discordSnowflakeSchema,
  createdAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
  /** Discord finalized it, and the last read stored the final votes. */
  final: z.boolean(),
  guildId: discordSnowflakeSchema,
  id: z.string().min(1),
  lastReadAt: z.iso.datetime().nullable(),
  /** The bot can no longer read the poll's channel: its votes stop at the last read. */
  noAccess: z.boolean(),
  question: z.string(),
  status: z.enum(["open", "ended"]),
  /** How many members voted, at the last read. */
  voters: z.number().int().min(0)
});
var adminPollListSchema = z.object({
  data: z.array(adminPollSchema),
  nextCursor: z.string().nullable(),
  /** Owners and admins post, end, refresh and export; anyone else only reads. */
  writable: z.boolean()
});
var adminMcpTokenSchema = z.object({
  createdAt: z.iso.datetime(),
  id: z.string().min(1),
  label: z.string().min(1).max(100),
  lastUsedAt: z.iso.datetime().nullable()
});
var adminMcpTokenListSchema = z.object({
  /** Owners and admins connect; a read-only account or a team member is told why it cannot. */
  canConnect: z.boolean(),
  tokens: z.array(adminMcpTokenSchema),
  /** The MCP endpoint, `${ADMIN_PUBLIC_URL}/mcp`. */
  url: z.url()
});
var adminMcpTokenCreateRequestSchema = z.object({
  label: z.string().trim().min(1).max(100)
});
var adminMcpTokenCreatedSchema = adminMcpTokenSchema.extend({
  token: z.string().regex(/^tmcp_[A-Za-z0-9_-]{43}$/)
});
var adminMcpOauthRequestSchema = z.object({
  businessName: z.string(),
  /** Owners and admins approve; anyone else may only cancel. */
  canConnect: z.boolean(),
  clientName: z.string(),
  /** Where the browser goes back to with the answer, so people can tell who is asking. */
  redirectHost: z.string()
});
var adminMcpOauthDecisionSchema = z.object({ redirectTo: z.url() });
var adminMcpConnectionListSchema = z.object({
  connections: z.array(
    z.object({
      clientName: z.string(),
      connectedAt: z.iso.datetime(),
      id: z.string().min(1),
      lastUsedAt: z.iso.datetime().nullable()
    })
  )
});
var adminMcpTeamSchema = z.object({
  people: z.array(
    z.object({
      adminId: z.string().min(1),
      connections: adminMcpConnectionListSchema.shape.connections,
      email: z.string(),
      tokens: z.array(adminMcpTokenSchema)
    })
  )
});
var mcpClientRegistrationRequestSchema = z.object({
  client_name: z.string().trim().min(1).max(100).optional(),
  grant_types: z.array(z.enum(["authorization_code", "refresh_token"])).optional(),
  redirect_uris: z.array(z.string().max(2048)).min(1).max(5),
  response_types: z.array(z.literal("code")).optional(),
  token_endpoint_auth_method: z.enum(["none", "client_secret_post"]).optional()
});
var mcpCursorSchema = z.string().min(1).max(200).describe("The nextCursor of the previous page; leave it out for the first page.");
var mcpDaySchema = (what) => z.iso.date().describe(`${what}, as YYYY-MM-DD (UTC).`);
var mcpListMembersInputSchema = z.strictObject({
  cursor: mcpCursorSchema.optional(),
  inServer: z.boolean().optional().describe(
    "Only members in the Discord server (true) or not in it (false); needs member tracking."
  ),
  limit: z.number().int().min(1).max(200).default(50).describe("Members per page, at most 200."),
  search: z.string().trim().min(1).max(100).optional().describe("Words of a name, an email, a Discord username, or a whole Discord ID."),
  signedUpFrom: mcpDaySchema("Signed up on or after this day").optional(),
  signedUpTo: mcpDaySchema("Signed up on or before this day").optional()
});
var mcpGetMemberInputSchema = z.strictObject({
  discordId: discordSnowflakeSchema.optional().describe("The member's Discord ID."),
  email: z.string().trim().min(3).max(320).optional().describe("The member's email.")
}).refine(
  (input) => input.discordId === void 0 !== (input.email === void 0),
  "Give either an email or a Discord ID."
);
var mcpListPollsInputSchema = z.strictObject({
  cursor: mcpCursorSchema.optional(),
  status: z.enum(["open", "ended"]).optional().describe("Only open or only ended polls.")
});
var mcpGetPollVotesInputSchema = z.strictObject({
  cursor: mcpCursorSchema.optional(),
  limit: z.number().int().min(1).max(1e3).default(200).describe("Voters per page, at most 1,000."),
  pollId: objectIdSchema.describe("A poll's id, from list_polls.")
});
var mcpListSavedPostsInputSchema = z.strictObject({
  channelId: discordSnowflakeSchema.optional().describe("Only posts of this saved channel."),
  cursor: mcpCursorSchema.optional(),
  from: mcpDaySchema("Posted on or after this day").optional(),
  limit: z.number().int().min(1).max(200).default(50).describe("Posts per page, at most 200."),
  search: z.string().trim().min(1).max(100).optional().describe("Text the post contains."),
  sort: z.enum(["time", "channel"]).default("time").describe("time: newest first; channel: by channel, then newest first."),
  to: mcpDaySchema("Posted on or before this day").optional()
});
var mcpGetSavedPostInputSchema = z.strictObject({
  includeImages: z.boolean().default(true).describe("Attach up to 4 of the post's and its replies' images, each at most 1 MB."),
  postId: objectIdSchema.describe("A saved post's id, from list_saved_posts.")
});
var adminPollChannelsSchema = z.object({
  channels: z.array(
    z.object({
      id: discordSnowflakeSchema,
      missing: z.array(
        z.enum(["View Channel", "Send Messages", "Send Polls", "Read Message History"])
      ),
      name: z.string(),
      /** Of `missing`, what the bot lacks server-wide too (the rest is the channel's overwrites). */
      serverMissing: z.array(
        z.enum(["View Channel", "Send Messages", "Send Polls", "Read Message History"])
      )
    })
  )
});
var botProfileLimits = { bio: 190, nick: 32 };
var adminBotProfileRequestSchema = z.object({
  avatar: z.enum(["logo", "custom", "none"]),
  banner: z.enum(["background", "custom", "none"]),
  // Characters, as Discord counts them, not UTF-16 units: an emoji is one.
  bio: z.string().trim().min(1).refine((value) => Array.from(value).length <= botProfileLimits.bio).nullable(),
  nick: z.string().trim().min(1).refine((value) => Array.from(value).length <= botProfileLimits.nick).nullable()
});
var adminBotProfileSchema = z.object({
  /** When the draft was last sent to Discord, and by which admin; null until then or after Reset. */
  applied: z.object({ at: z.iso.datetime(), by: z.string().min(1) }).nullable(),
  /** The bot holds Change Nickname in the server; false when Discord cannot say. */
  canChangeNickname: z.boolean(),
  draft: adminBotProfileRequestSchema,
  /** The images each choice uses, for the preview; null where there is none. */
  images: z.object({
    background: z.string().nullable(),
    customAvatar: z.string().nullable(),
    customBanner: z.string().nullable(),
    logo: z.string().nullable()
  }),
  /** Discord's code and status of the last failed Apply; never a message. */
  lastError: z.object({ code: z.number().int().nullable(), status: z.number().int().nullable() }).nullable(),
  /** The bot's profile in the server now; null when Discord cannot be read. */
  live: z.object({
    avatarUrl: z.string().nullable(),
    bannerUrl: z.string().nullable(),
    nick: z.string().nullable()
  }).nullable(),
  /** This account may save, apply and reset it (an owner or admin). */
  writable: z.boolean()
});
var adminBotProfileApplySchema = z.object({
  applied: z.object({ at: z.iso.datetime(), by: z.string().min(1) }).nullable(),
  skipped: z.array(z.literal("nick"))
});
var adminDiscordConnectionSchema = z.object({
  bot: z.object({
    publishAvailable: z.boolean(),
    reactionChannelId: z.string(),
    reactionMessage: z.string(),
    reactionRolesEnabled: z.boolean(),
    reactions: z.array(adminDiscordReactionRoleSchema),
    welcomeChannelId: z.string(),
    welcomeEnabled: z.boolean(),
    welcomeMessage: z.string()
  }),
  /** Community data; present only while a community feature is on (`members`: member tracking). */
  community: z.object({
    /** COMMUNITY_FEATURES bot_profile: the Discord page offers the bot's server profile. */
    botProfile: z.literal(true).optional(),
    members: adminMemberTrackingSchema.optional(),
    /** COMMUNITY_FEATURES polls: the Discord page has a Polls tab. */
    polls: z.literal(true).optional(),
    /** COMMUNITY_FEATURES saved_posts: the Discord page has a Saved posts tab. */
    savedPosts: z.literal(true).optional()
  }).optional(),
  connection: z.object({
    clientId: z.string(),
    connectAvailable: z.boolean(),
    connectUrl: z.url().nullable(),
    connected: z.boolean(),
    manageAvailable: z.boolean(),
    serverId: z.string(),
    serverName: z.string().nullable()
  }),
  embeds: z.object({
    contactUrl: z.string(),
    expiredMessage: z.string(),
    failedMessage: z.string(),
    videoUrl: z.string(),
    welcomeMessage: z.string()
  }),
  guilds: z.array(adminDiscordGuildSchema).default([]),
  mode: z.literal("live"),
  roles: z.array(adminDiscordRoleSchema),
  webhooks: z.object({
    logs: z.string(),
    rewards: z.string()
  }),
  writable: z.boolean()
});
var adminUpdateDiscordWebhooksRequestSchema = z.object({
  logs: z.url(),
  rewards: z.url(),
  serverId: z.string().trim().min(1).max(64).optional()
});
var adminUpdateDiscordBotRequestSchema = z.object({
  reactionChannelId: z.string().trim().max(64),
  reactionMessage: z.string().trim().max(2e3),
  reactionRolesEnabled: z.boolean(),
  reactions: z.array(adminDiscordReactionRoleSchema).max(25),
  welcomeChannelId: z.string().trim().max(64),
  welcomeEnabled: z.boolean(),
  welcomeMessage: z.string().trim().max(2e3)
}).superRefine((settings, context) => {
  if (settings.welcomeEnabled && (!settings.welcomeChannelId || !settings.welcomeMessage)) {
    context.addIssue({
      code: "custom",
      message: "Choose a welcome channel and enter a welcome message."
    });
  }
  if (settings.reactionRolesEnabled && (!settings.reactionChannelId || !settings.reactionMessage || settings.reactions.length === 0)) {
    context.addIssue({
      code: "custom",
      message: "Choose a reaction channel, message, and at least one role."
    });
  }
  if (new Set(settings.reactions.map(({ emoji }) => emoji)).size !== settings.reactions.length) {
    context.addIssue({
      code: "custom",
      message: "Each emoji can only grant one role.",
      path: ["reactions"]
    });
  }
});
var adminUpdateDiscordEmbedsRequestSchema = z.object({
  contactUrl: z.url(),
  expiredMessage: z.string().trim().min(1).max(2e3),
  failedMessage: z.string().trim().min(1).max(2e3),
  videoUrl: z.union([z.literal(""), z.url()]),
  welcomeMessage: z.string().trim().min(1).max(2e3)
});
var adminSafeHexColorSchema = hexColorSchema;
var adminSafeHttpUrlSchema = httpUrlSchema;
var adminBrandingAssetUrlSchema = safePublicAssetUrlSchema;
var adminSidebarLinkSchema = z.object({
  name: z.string().trim().min(1).max(80),
  url: adminSafeHttpUrlSchema
});
var adminBrandingThemeSchema = z.enum(["light", "dark", "system"]);
var adminBrandingSettingsSchema = z.object({
  assetUploadsAvailable: z.boolean(),
  backgroundUrl: safePublicAssetUrlSchema.nullable(),
  backgroundVideoUrl: backgroundVideoUrlSchema.nullable().default(null),
  groupLogoUrl: safePublicAssetUrlSchema.nullable(),
  groupName: z.string().min(1),
  legal: adminLegalStatusSchema,
  mode: z.literal("live"),
  primaryColor: adminSafeHexColorSchema,
  privacyUrl: z.string().default(""),
  secondaryColor: adminSafeHexColorSchema,
  sidebarLinks: z.array(adminSidebarLinkSchema),
  /** The business chose Ternary's standard terms and privacy (G5). */
  standardLegal: z.boolean().default(false),
  termsUrl: z.string(),
  theme: adminBrandingThemeSchema.default("system"),
  welcomeEmail: z.string(),
  writable: z.boolean()
});
var adminClearableLegalUrlSchema = adminSafeHttpUrlSchema.nullable().transform((value) => value ?? "");
var adminUpdateBrandingRequestSchema = z.object({
  backgroundUrl: adminBrandingAssetUrlSchema.nullable(),
  // Left out keeps the saved video link; null clears it.
  backgroundVideoUrl: backgroundVideoUrlSchema.nullable().optional(),
  groupLogoUrl: adminBrandingAssetUrlSchema.nullable(),
  groupName: z.string().trim().min(1).max(120),
  primaryColor: adminSafeHexColorSchema,
  // Left out keeps the saved privacy link; null clears it.
  privacyUrl: adminClearableLegalUrlSchema.optional(),
  secondaryColor: adminSafeHexColorSchema,
  sidebarLinks: z.array(adminSidebarLinkSchema).max(20),
  // Left out keeps the saved choice. admin-api refuses true while the standard terms are off.
  standardLegal: z.boolean().optional(),
  termsUrl: adminClearableLegalUrlSchema,
  theme: adminBrandingThemeSchema.optional()
});
var adminLegalLinkCheckRequestSchema = z.object({
  url: z.string().max(2048).pipe(httpUrlSchema)
});
var adminLegalLinkCheckSchema = z.object({
  reachable: z.boolean(),
  reason: z.enum(["unconfirmed", "unreachable"]).optional()
});
var adminUpdateWelcomeEmailRequestSchema = z.object({
  welcomeEmail: z.string().trim().min(1).max(2e4)
});
var adminTeamAccessSchema = z.enum(["admin", "write", "read"]);
var adminTeamMemberSchema = z.object({
  access: adminTeamAccessSchema,
  email: z.email(),
  id: z.string().min(1),
  status: z.enum(["created", "pending"])
});
var adminTeamCatalogSchema = z.object({
  inviteDeliveryAvailable: z.boolean(),
  members: z.array(adminTeamMemberSchema),
  mode: z.literal("live"),
  writable: z.boolean()
});
var adminCreateTeamMemberRequestSchema = z.object({
  access: z.enum(["write", "read"]),
  email: z.email()
});
var adminTeamSelectionRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100)
});
var adminDomainHostnameSchema = z.string().trim().toLowerCase().min(4).max(253).regex(
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  "Enter a valid domain without a protocol or path."
);
var adminDomainStateSchema = z.enum([
  "pending",
  "conflict",
  "issuing",
  "live",
  "failing",
  "expired"
]);
var adminDomainRecordSchema = z.object({
  name: z.string().min(1),
  type: z.enum(["CNAME", "TXT"]),
  value: z.string().min(1)
});
var adminDomainSchema = z.object({
  /** The CNAME target, as the legacy settings routes call it `dns`. */
  cname: z.string().min(1),
  /** The business's primary address. */
  default: z.boolean(),
  hostname: z.string().min(1),
  /** What DNS showed at the last check, next to what `records` expects; null before one. */
  observed: z.object({
    checkedAt: z.iso.datetime(),
    cname: z.array(z.string()),
    txt: z.array(z.string())
  }).nullable(),
  records: z.array(adminDomainRecordSchema),
  /** Why a domain whose records are right still waits, such as the live cap (F5). */
  reason: z.string().min(1).optional(),
  state: adminDomainStateSchema,
  /** `verified` while it is served (live or failing). */
  status: z.enum(["verified", "unverified"])
});
var adminDomainCatalogSchema = z.object({
  /** The business's Ternary address (Settings → Address), which can be made primary again. */
  automatic: z.object({ default: z.boolean(), hostname: z.string().min(1) }).nullable(),
  defaultUrl: z.url(),
  domains: z.array(adminDomainSchema),
  mode: z.literal("live"),
  provider: z.enum(["managed", "unavailable"]),
  writable: z.boolean()
});
var adminDomainRequestSchema = z.object({
  hostname: adminDomainHostnameSchema,
  /** Made the business's address once live; true when left out. */
  primary: z.boolean().optional()
});
var adminAccountCardSchema = z.object({
  brand: z.string().min(1).max(32),
  default: z.boolean(),
  id: z.string().min(1),
  last4: z.string().regex(/^\d{4}$/)
});
var adminAccountCatalogSchema = z.object({
  cards: z.array(adminAccountCardSchema),
  currency: z.string().regex(/^[A-Z]{3}$/),
  email: z.email(),
  interval: z.enum(["day", "week", "month", "year"]),
  intervalCount: z.number().int().positive(),
  mode: z.literal("live"),
  nextInvoiceMinor: z.number().int().nonnegative(),
  provider: z.enum(["stripe", "unavailable"]),
  publishableKey: z.string().min(1).nullable(),
  renewsAt: z.iso.datetime().nullable(),
  status: z.enum(["active", "trialing", "cancelling", "past_due", "unpaid", "canceled", "unknown"]),
  tier: z.string().min(1),
  /** P3: on Basic (the free plan), the paid plans it can upgrade to; absent on a paid plan. */
  upgradePlans: z.array(adminRegistrationPlanSchema).optional(),
  /**
   * P4-F1: the business is inactive until its first payment, left waiting on the bank's check, is
   * finished: Account offers "Finish your payment".
   */
  firstPaymentPending: z.literal(true).optional(),
  /** N-b: that payment renews the business, so it goes live again; never "first". */
  pendingRenewal: z.literal(true).optional(),
  writable: z.boolean()
});
var adminAccountUpgradeRequestSchema = z.object({
  paymentMethodId: z.string().trim().regex(/^pm_[A-Za-z0-9_]+$/),
  priceId: z.string().regex(/^price_[A-Za-z0-9]+$/)
});
var adminAccountUpgradeActionSchema = z.object({
  paymentAction: z.object({ clientSecret: z.string().min(1) })
});
var adminAccountPaymentSchema = z.object({
  active: z.boolean(),
  paymentAction: z.object({ clientSecret: z.string().min(1) }).optional(),
  /** P4-F1: the payment can no longer be finished (voided, cancelled, expired): start it again. */
  restartable: z.literal(true).optional()
});
var adminAccountCardRequestSchema = z.object({
  paymentMethodId: z.string().trim().regex(/^(?:pm|card)_[A-Za-z0-9_]+$/)
});
var adminAccountSetupIntentSchema = z.object({
  clientSecret: z.string().min(1)
});
var adminPaymentTaxRateSchema = z.object({
  displayName: z.string().min(1),
  id: z.string().min(1),
  inclusive: z.boolean(),
  percentage: z.number().min(0).max(100)
});
var adminPaymentGatewayCatalogSchema = z.object({
  connectAvailable: z.boolean(),
  connected: z.boolean(),
  coupons: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1)
    })
  ),
  mode: z.literal("live"),
  provider: z.enum(["stripe", "none"]),
  referralCouponId: z.string().nullable(),
  requireBillingAddress: z.boolean(),
  requireLoginForPurchase: z.boolean(),
  selectedTaxRateId: z.string().nullable(),
  taxRates: z.array(adminPaymentTaxRateSchema),
  writable: z.boolean()
});
var adminUpdatePaymentGatewayRequestSchema = z.object({
  newTaxRate: z.object({
    displayName: z.string().trim().min(1).max(100),
    inclusive: z.boolean(),
    percentage: z.number().min(0.01).max(100)
  }).nullable(),
  referralCouponId: z.string().trim().min(1).max(128).nullable(),
  requireBillingAddress: z.boolean(),
  requireLoginForPurchase: z.boolean(),
  taxRateId: z.union([
    z.null(),
    z.literal("new"),
    z.string().trim().regex(/^txr_[A-Za-z0-9_]+$/)
  ])
}).superRefine((value, context) => {
  if (value.taxRateId === "new" && !value.newTaxRate) {
    context.addIssue({
      code: "custom",
      message: "Enter the new tax rate details.",
      path: ["newTaxRate"]
    });
  }
});
var adminPaymentGatewayConnectSchema = z.object({
  url: z.url()
});
var adminTrackingPixelsSchema = z.object({
  mode: z.literal("live"),
  pixels: trackingPixelsSchema,
  writable: z.boolean()
});
var adminUpdateTrackingPixelsRequestSchema = trackingPixelsSchema;
var adminDeveloperKeySchema = z.object({
  createdAt: z.iso.datetime(),
  maskedKey: z.string().min(1),
  migrationRequired: z.boolean()
});
var adminDeveloperSettingsSchema = z.object({
  key: adminDeveloperKeySchema.nullable(),
  /** The Claude (MCP) section exists (COMMUNITY_FEATURES `mcp`). Absent otherwise. */
  mcp: z.literal(true).optional(),
  mode: z.literal("live"),
  writable: z.boolean()
});
var adminDeveloperKeyIssuedSchema = z.object({
  key: adminDeveloperKeySchema,
  secretKey: z.string().regex(/^sk_(?:test|live)_[A-Za-z0-9_-]{32,}$/)
});
var developerApiAddressSchema = z.record(z.string(), z.unknown()).nullable();
var developerApiMemberSchema = z.object({
  customer: z.object({
    address: developerApiAddressSchema,
    email: z.email(),
    id: z.string().min(1),
    name: z.string()
  }),
  discord: z.object({
    avatar: z.string().optional(),
    id: z.string().optional(),
    username: z.string().optional()
  }),
  key: z.string().min(1),
  metadata: z.record(z.string(), z.unknown()),
  referral: z.object({
    referral_code: z.string(),
    referred: z.number().int().nonnegative()
  }),
  subscriptions: z.array(
    z.object({
      id: z.string().min(1),
      plan: z.string().min(1)
    })
  )
});
var developerApiProductSchema = z.object({
  add_ons: z.array(z.string()),
  amount: z.number().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  discord: z.object({ roles: z.array(z.string()) }),
  name: z.string().min(1),
  password: z.string().nullable(),
  price: z.string().min(1),
  product: z.string().min(1),
  recurring: z.union([
    z.object({
      interval: z.enum(["day", "week", "month", "year"]),
      interval_count: z.number().int().positive(),
      trial_days: z.number().int().nonnegative()
    }),
    z.object({}).strict()
  ]),
  stock: z.number().int().nonnegative(),
  upgrades: z.array(z.string())
});
var developerApiMetadataRequestSchema = z.object({
  metadata: z.record(z.string().min(1).max(120), z.unknown()).refine((metadata) => Object.keys(metadata).length <= 100)
});
var adminBrandingAssetSchema = z.object({
  url: safePublicAssetUrlSchema
});
var apiErrorSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string().min(1)
  })
});
var signupStepSchema = z.enum([
  "details",
  "verify_email",
  "discord",
  "confirm_discord",
  "confirm_switch",
  "join",
  "done"
]);
var signupBrandingSchema = z.object({
  checkoutBackgroundUrl: safePublicAssetUrlSchema.nullable(),
  // Plays behind the brand panel; a page cached from before it reads none.
  checkoutBackgroundVideoUrl: backgroundVideoUrlSchema.nullable().default(null),
  companyName: z.string().min(1).max(120),
  logoUrl: safePublicAssetUrlSchema.nullable(),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  theme: adminBrandingThemeSchema
});
var signupProductSchema = z.object({
  description: z.string().max(2e3),
  imageUrl: productImageUrlSchema.nullable(),
  name: z.string().min(1).max(120)
});
var signupPageSchema = z.object({
  branding: signupBrandingSchema,
  fields: signupFieldsSchema,
  legal: z.object({
    privacyUrl: httpUrlSchema,
    termsUrl: httpUrlSchema
  }),
  pixels: trackingPixelsSchema,
  product: signupProductSchema,
  steps: z.tuple([z.literal("details"), z.literal("discord"), z.literal("join")])
});
var signupStartRequestSchema = z.object({
  touchId: objectIdSchema.optional()
});
var signupStartResponseSchema = z.object({
  signupId: signupIdSchema
});
var signupAnswerValueSchema = z.union([z.string(), z.array(z.string()), z.boolean()]);
var signupConsentSchema = z.object({
  ads: z.boolean(),
  terms: z.literal(true)
});
var signupDetailsRequestSchema = z.object({
  answers: z.record(signupFieldKeySchema, signupAnswerValueSchema),
  consent: signupConsentSchema,
  email: z.string().trim().pipe(z.email().max(254)),
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120)
});
var maskedEmailSchema = z.string().regex(/^[^\s@]\*{3}@[^\s@]+$/);
var signupDetailsResponseSchema = z.object({
  otpSentTo: maskedEmailSchema
});
var signupVerifyEmailRequestSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code.")
});
var signupVerifyEmailResponseSchema = z.object({
  verified: z.literal(true)
});
var signupJoinResponseSchema = z.object({
  guildUrl: httpUrlSchema,
  joined: z.literal(true)
});
var signupDiscordConfirmRequestSchema = signupVerifyEmailRequestSchema;
var signupDiscordLinkResponseSchema = z.object({
  linked: z.literal(true)
});
var discordAvatarUrlSchema = z.string().regex(
  /^https:\/\/cdn\.discordapp\.com\/avatars\/\d{15,22}\/[A-Za-z0-9_]{1,64}\.png(\?size=\d{1,4})?$/
);
var signupStateSchema = z.object({
  branding: signupBrandingSchema,
  discord: z.strictObject({
    avatarUrl: discordAvatarUrlSchema.nullable(),
    username: z.string().min(1)
  }).optional(),
  email: maskedEmailSchema.optional(),
  guildUrl: httpUrlSchema.nullable(),
  joinedAt: z.iso.datetime().optional(),
  pixels: trackingPixelsSchema,
  product: signupProductSchema,
  /**
   * Member tracking only: a finished signup's member left the business's server, so the join page
   * offers Join again. Absent otherwise, so every other answer is as before.
   */
  rejoin: z.literal(true).optional(),
  slug: signupSlugSchema,
  step: signupStepSchema
});
var memberHomeSchema = z.object({
  avatarUrl: discordAvatarUrlSchema.nullable(),
  branding: signupBrandingSchema,
  displayName: z.string().min(1),
  products: z.array(
    z.object({
      guildUrl: httpUrlSchema,
      id: objectIdSchema,
      imageUrl: productImageUrlSchema.nullable(),
      joinedAt: z.iso.datetime(),
      name: z.string().min(1)
    })
  )
});
var signupErrorCodeSchema = z.enum([
  "not_found",
  "invalid_request",
  "wrong_step",
  "rate_limited",
  "unavailable",
  "email_unavailable",
  "code_invalid",
  "code_expired",
  "too_many_attempts",
  "too_many_sends",
  "discord_denied",
  "discord_in_use",
  "discord_not_linked",
  "discord_link_expired",
  "guild_unavailable",
  "join_failed",
  "not_signed_in"
]);
var relativePathBase = "https://relative-path.invalid";
var sameOriginPathSchema = z.string().refine(
  (value) => value.startsWith("/") && URL.canParse(value, relativePathBase) && new URL(value, relativePathBase).origin === relativePathBase
);
var signupErrorSchema = z.object({
  attemptsRemaining: z.number().int().min(0).max(4).optional(),
  authorizationUrl: z.union([sameOriginPathSchema, httpUrlSchema]).optional(),
  error: signupErrorCodeSchema,
  message: z.string().min(1)
});
function signupAnswerSchema(field) {
  const options = field.options ?? [];
  switch (field.kind) {
    case "checkbox":
      return field.required ? z.literal(true) : z.boolean();
    case "country":
      return z.string().regex(/^[A-Z]{2}$/);
    case "date":
      return z.iso.date();
    case "multiselect":
      return z.array(z.enum(options)).min(field.required ? 1 : 0).refine(uniqueValues);
    case "phone":
      return z.string().trim().regex(/^\+?\(?[0-9][0-9 ().-]{4,23}$/);
    case "select":
      return z.enum(options);
    case "text":
      return z.string().trim().min(field.required ? 1 : 0).max(500);
  }
}
function buildSignupAnswersSchema(fields) {
  const shape = Object.fromEntries(
    fields.map((field) => {
      const answer = signupAnswerSchema(field);
      return [field.key, field.required ? answer : answer.optional()];
    })
  );
  return z.strictObject(shape);
}
function pixelEventId(signupId, event) {
  return `${signupId}:${event}`;
}
var utmValueSchema = z.string().trim().max(200).optional();
var trackingIdentifierSchema = z.string().max(500).optional();
var touchRequestSchema = z.object({
  clickIds: z.object({
    fbclid: trackingIdentifierSchema,
    gbraid: trackingIdentifierSchema,
    gclid: trackingIdentifierSchema,
    ttclid: trackingIdentifierSchema,
    wbraid: trackingIdentifierSchema
  }),
  consent: z.object({ ads: z.boolean() }),
  cookies: z.object({
    fbc: trackingIdentifierSchema,
    fbp: trackingIdentifierSchema,
    ttp: trackingIdentifierSchema
  }),
  landingUrl: httpUrlSchema.max(2048),
  referrer: z.string().max(2048).optional(),
  utm: z.object({
    campaign: utmValueSchema,
    content: utmValueSchema,
    medium: utmValueSchema,
    source: utmValueSchema,
    term: utmValueSchema
  })
});
var touchResponseSchema = z.object({
  touchId: objectIdSchema
});
var touchConsentRequestSchema = z.object({
  ads: z.boolean()
});
var journeyStepSchema = z.enum(["details", "discord", "join"]);
var journeyRequestSchema = z.object({
  event: z.enum(["landing", "step_view"]),
  signupId: signupIdSchema.optional(),
  step: journeyStepSchema.optional(),
  touchId: objectIdSchema.optional()
}).superRefine((journey, context) => {
  if (journey.event === "step_view" && !journey.step) {
    context.addIssue({ code: "custom", message: "A step view needs its step.", path: ["step"] });
  }
  if (!journey.touchId && !journey.signupId) {
    context.addIssue({ code: "custom", message: "Send a touchId or a signupId." });
  }
});
var acquisitionChannelSchema = z.enum([
  "meta",
  "tiktok",
  "google",
  "x",
  "discord",
  "referral",
  "direct"
]);
var journeyEventNameSchema = z.enum([
  "landing",
  "step_view",
  "form_start",
  "details_submitted",
  "email_verified",
  "discord_started",
  "discord_authorized",
  "joined",
  "join_failed"
]);
var joinFailureReasonSchema = z.enum(["role_hierarchy", "missing_permission"]);
var adminCustomerSortSchema = z.enum(["signedUpAt", "joinedAt", "name"]);
var withoutNul = (value) => !value.includes("\0");
var adminCustomerListQuerySchema = z.object({
  campaign: z.string().min(1).max(200).refine(withoutNul).optional(),
  channel: acquisitionChannelSchema.optional(),
  cursor: z.string().max(200).optional(),
  dir: z.enum(["asc", "desc"]).default("desc"),
  from: z.iso.datetime().optional(),
  /**
   * Member tracking: `yes` or `no`, checked and honoured only for a business that tracks members.
   * Any other business ignores it, as before the field existed, so any string passes here.
   */
  inServer: z.union([z.string(), z.array(z.string())]).optional(),
  joined: z.enum(["yes", "no"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  productId: objectIdSchema.optional(),
  search: z.string().trim().min(1).max(200).refine(withoutNul).optional(),
  sort: adminCustomerSortSchema.default("signedUpAt"),
  source: z.string().min(1).max(200).refine(withoutNul).optional(),
  to: z.iso.datetime().optional()
});
var adminCustomerProductSchema = z.object({
  id: objectIdSchema,
  name: z.string().min(1)
});
var adminCustomerRowSchema = z.object({
  campaign: z.string().nullable(),
  channel: acquisitionChannelSchema.nullable(),
  discordAvatarUrl: z.url().nullable(),
  discordDisplayName: z.string().nullable(),
  discordId: discordSnowflakeSchema.nullable(),
  discordUsername: z.string().nullable(),
  // The customers collection's own email pattern, so every stored customer can be listed.
  email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
  firstName: z.string(),
  id: objectIdSchema,
  /** Member tracking only: whether the customer is in the business's server now. */
  inServer: z.boolean().optional(),
  joinedAt: z.iso.datetime().nullable(),
  lastName: z.string(),
  /** Member tracking only: when a customer who is not in the server last left it. */
  leftServerAt: z.iso.datetime().nullable().optional(),
  products: z.array(adminCustomerProductSchema),
  signedUpAt: z.iso.datetime(),
  source: z.string().nullable()
});
var adminCustomerListSchema = z.object({
  canExport: z.boolean(),
  data: z.array(adminCustomerRowSchema),
  /** Member tracking only: when the business's members were last checked, and how. */
  members: z.object({
    checkedAt: z.iso.datetime().nullable(),
    mode: z.enum(["list", "probe"]).nullable(),
    tracking: z.literal(true)
  }).optional(),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative()
});
var adminCustomerFacetsSchema = z.object({
  campaigns: z.array(z.string()),
  channels: z.array(acquisitionChannelSchema),
  products: z.array(adminCustomerProductSchema),
  sources: z.array(z.string())
});
var adminTouchSchema = z.object({
  channel: acquisitionChannelSchema,
  clickIds: z.object({
    fbclid: z.string().nullable(),
    gbraid: z.string().nullable(),
    gclid: z.string().nullable(),
    ttclid: z.string().nullable(),
    wbraid: z.string().nullable()
  }),
  consent: z.object({ ads: z.boolean() }),
  createdAt: z.iso.datetime(),
  id: objectIdSchema,
  landingUrl: httpUrlSchema,
  referrer: z.string().nullable(),
  utm: z.object({
    campaign: z.string().nullable(),
    content: z.string().nullable(),
    medium: z.string().nullable(),
    source: z.string().nullable(),
    term: z.string().nullable()
  })
});
var adminCustomerRecordSchema = adminCustomerRowSchema.extend({
  acquisition: z.object({
    campaign: z.string().nullable(),
    channel: acquisitionChannelSchema,
    firstTouch: adminTouchSchema.nullable(),
    lastTouch: adminTouchSchema.nullable(),
    medium: z.string().nullable(),
    source: z.string().nullable()
  }).nullable(),
  answers: z.record(z.string(), z.string()),
  consent: z.object({
    ads: z.boolean(),
    terms: z.iso.datetime(),
    // The IP the terms were accepted from, for the business's audit; unlike a touch's network
    // data it is shown. Null when it was not recorded, as an answer from before it existed reads.
    termsIp: z.string().nullable().default(null),
    termsVersion: z.string().min(1)
  }).nullable(),
  country: z.string().nullable(),
  phone: z.string().nullable()
});
var adminJourneyEventSchema = z.object({
  at: z.iso.datetime(),
  event: journeyEventNameSchema,
  id: objectIdSchema,
  /** On a `join_failed` event whose refusal the bot's settings explain; absent otherwise. */
  reason: joinFailureReasonSchema.optional(),
  signupId: signupIdSchema.nullable(),
  step: journeyStepSchema.nullable(),
  touchId: objectIdSchema.nullable()
});
var adminCustomerDetailSchema = z.object({
  customer: adminCustomerRecordSchema,
  timeline: z.array(adminJourneyEventSchema)
});
var adminDashboardRangeSchema = z.enum(["today", "7d", "30d"]);
function isTimeZone(value) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
var adminDashboardQuerySchema = z.object({
  range: adminDashboardRangeSchema,
  tz: z.string().refine(isTimeZone, "Choose a valid time zone.").default("UTC")
});
var adminFunnelStageSchema = z.enum([
  "landing",
  "form_start",
  "details_submitted",
  "email_verified",
  "discord_authorized",
  "joined"
]);
var countSchema = z.number().int().nonnegative();
var inOrder = (values, expected) => values.length === expected.length && values.every((value, index) => value === expected[index]);
var adminDashboardSummarySchema = z.object({
  byChannel: z.array(
    z.object({ channel: acquisitionChannelSchema, joins: countSchema, signups: countSchema })
  ).refine(
    (rows) => inOrder(
      rows.map((row) => row.channel),
      acquisitionChannelSchema.options
    ),
    "List every channel in order."
  ),
  funnel: z.array(z.object({ count: countSchema, step: adminFunnelStageSchema })).refine(
    (rows) => inOrder(
      rows.map((row) => row.step),
      adminFunnelStageSchema.options
    ),
    "List every funnel stage in order."
  ),
  joins: countSchema,
  signups: countSchema
});
export {
  acquisitionChannelSchema,
  adminAccountCardRequestSchema,
  adminAccountCardSchema,
  adminAccountCatalogSchema,
  adminAccountPaymentSchema,
  adminAccountSetupIntentSchema,
  adminAccountUpgradeActionSchema,
  adminAccountUpgradeRequestSchema,
  adminAddressCheckSchema,
  adminAddressRequestSchema,
  adminAddressSchema,
  adminAlertCatalogSchema,
  adminAlertSchema,
  adminAuthActionResponseSchema,
  adminAuthTokenStatusSchema,
  adminBotHealthSchema,
  adminBotProfileApplySchema,
  adminBotProfileRequestSchema,
  adminBotProfileSchema,
  adminBrandingAssetSchema,
  adminBrandingSettingsSchema,
  adminBrandingThemeSchema,
  adminCouponCatalogSchema,
  adminCouponSchema,
  adminCreateCouponRequestSchema,
  adminCreateDiscordRoleRequestSchema,
  adminCreateFreeProductRequestSchema,
  adminCreateMemberRequestSchema,
  adminCreateProductRequestSchema,
  adminCreateRewardRequestSchema,
  adminCreateTeamMemberRequestSchema,
  adminCredentialTokenSchema,
  adminCustomerDetailSchema,
  adminCustomerFacetsSchema,
  adminCustomerListQuerySchema,
  adminCustomerListSchema,
  adminCustomerRecordSchema,
  adminCustomerRowSchema,
  adminCustomerSortSchema,
  adminDashboardQuerySchema,
  adminDashboardRangeSchema,
  adminDashboardSummarySchema,
  adminDeleteMembersRequestSchema,
  adminDeleteProductsRequestSchema,
  adminDeveloperKeyIssuedSchema,
  adminDeveloperKeySchema,
  adminDeveloperSettingsSchema,
  adminDiscordConnectionSchema,
  adminDiscordGuildSchema,
  adminDiscordMemberSchema,
  adminDiscordReactionRoleSchema,
  adminDiscordRoleSchema,
  adminDomainCatalogSchema,
  adminDomainHostnameSchema,
  adminDomainRecordSchema,
  adminDomainRequestSchema,
  adminDomainSchema,
  adminDomainStateSchema,
  adminForgotPasswordRequestSchema,
  adminFreeProductSchema,
  adminFunnelStageSchema,
  adminJourneyEventSchema,
  adminLegalLinkCheckRequestSchema,
  adminLegalLinkCheckSchema,
  adminLoginRequestSchema,
  adminLoginResponseSchema,
  adminMcpConnectionListSchema,
  adminMcpOauthDecisionSchema,
  adminMcpOauthRequestSchema,
  adminMcpTeamSchema,
  adminMcpTokenCreateRequestSchema,
  adminMcpTokenCreatedSchema,
  adminMcpTokenListSchema,
  adminMcpTokenSchema,
  adminMemberCatalogSchema,
  adminMemberDetailSchema,
  adminMemberSchema,
  adminMemberSubscriptionSchema,
  adminMemberTrackingRequestSchema,
  adminMemberTrackingSchema,
  adminOnboardingAccountRequestSchema,
  adminOnboardingBrandingRequestSchema,
  adminOnboardingCatalogSchema,
  adminOnboardingCompleteSchema,
  adminOnboardingConnectSchema,
  adminOnboardingDiscordRequestSchema,
  adminOnboardingLinkSchema,
  adminOnboardingPixelsRequestSchema,
  adminOnboardingStageSchema,
  adminOnboardingTokenSchema,
  adminOnboardingTrackingStatusSchema,
  adminPasswordActionRequestSchema,
  adminPaymentGatewayCatalogSchema,
  adminPaymentGatewayConnectSchema,
  adminPaymentTaxRateSchema,
  adminPollChannelsSchema,
  adminPollCreateRequestSchema,
  adminPollListSchema,
  adminPollSchema,
  adminProductCatalogSchema,
  adminProductGuildSchema,
  adminProductImageSchema,
  adminProductIntervalSchema,
  adminProductRemovalSchema,
  adminProductRequestSchema,
  adminProductSchema,
  adminProductTypeSchema,
  adminRegistrationAuthorizeSchema,
  adminRegistrationContextSchema,
  adminRegistrationCouponSchema,
  adminRegistrationEmailCodeRequestSchema,
  adminRegistrationEmailCodeSchema,
  adminRegistrationEmailVerifyRequestSchema,
  adminRegistrationPlanSchema,
  adminRegistrationRequestSchema,
  adminRegistrationResponseSchema,
  adminRegistrationSetupIntentRequestSchema,
  adminRegistrationSetupIntentSchema,
  adminRewardCatalogSchema,
  adminRewardSchema,
  adminRoleSchema,
  adminSavedChannelOptionSchema,
  adminSavedChannelsRequestSchema,
  adminSavedChannelsSchema,
  adminSavedPostDetailSchema,
  adminSavedPostListQuerySchema,
  adminSavedPostListSchema,
  adminSavedPostSchema,
  adminSessionResponseSchema,
  adminSessionSchema,
  adminSetupItemIdSchema,
  adminSetupSchema,
  adminSidebarLinkSchema,
  adminStatusSchema,
  adminTeamAccessSchema,
  adminTeamCatalogSchema,
  adminTeamMemberSchema,
  adminTeamSelectionRequestSchema,
  adminTouchSchema,
  adminTrackingPixelsSchema,
  adminUpdateBrandingRequestSchema,
  adminUpdateDiscordBotRequestSchema,
  adminUpdateDiscordEmbedsRequestSchema,
  adminUpdateDiscordWebhooksRequestSchema,
  adminUpdateFreeProductRequestSchema,
  adminUpdateMemberRequestSchema,
  adminUpdatePaymentGatewayRequestSchema,
  adminUpdateProductRequestSchema,
  adminUpdateTrackingPixelsRequestSchema,
  adminUpdateWelcomeEmailRequestSchema,
  apiErrorSchema,
  billingStripeEventSchema,
  billingStripeEventTypeSchema,
  billingWebhookResponseSchema,
  botProfileLimits,
  buildSignupAnswersSchema,
  canAssignDiscordRole,
  communityFeatures,
  customerContextSchema,
  customerDashboardAccountSchema,
  customerDashboardProductOptionSchema,
  customerMembershipActionResponseSchema,
  customerMembershipKeyRequestSchema,
  customerOrderCompletionSchema,
  customerPaymentMethodActionResponseSchema,
  customerPaymentMethodRequestSchema,
  customerPaymentMethodSetupSchema,
  customerPurchaseAgreementSchema,
  customerPurchaseCatalogSchema,
  customerPurchaseCouponSchema,
  customerPurchaseProductSchema,
  customerPurchaseRequestSchema,
  customerPurchaseResponseSchema,
  customerPurchaseSetupIntentSchema,
  customerRewardRedemptionRequestSchema,
  customerRewardRedemptionResponseSchema,
  customerSessionResponseSchema,
  customerSubscriptionActionResponseSchema,
  customerSubscriptionCancellationRequestSchema,
  customerSubscriptionIdSchema,
  customerSubscriptionPlanRequestSchema,
  customerTenantSchema,
  customerTerminalAccessRequestSchema,
  customerTerminalAccessResponseSchema,
  customerViewerSchema,
  defaultFreeProductSignupFields,
  developerApiMemberSchema,
  developerApiMetadataRequestSchema,
  developerApiProductSchema,
  discordAvatarUrlSchema,
  discordBotReach,
  discordChannelPermissions,
  discordPermission,
  discordSnowflakeSchema,
  healthResponseSchema,
  joinFailureReasonSchema,
  journeyEventNameSchema,
  journeyRequestSchema,
  journeyStepSchema,
  mcpClientRegistrationRequestSchema,
  mcpGetMemberInputSchema,
  mcpGetPollVotesInputSchema,
  mcpGetSavedPostInputSchema,
  mcpListMembersInputSchema,
  mcpListPollsInputSchema,
  mcpListSavedPostsInputSchema,
  memberHomeSchema,
  objectIdSchema,
  pixelEventId,
  pollChannelPermissions,
  pollEmoji,
  pollLimits,
  productAccessSchema,
  productImageUrlSchema,
  reservedTenantHandles,
  savedChannelsMax,
  serviceNameSchema,
  signupAnswerValueSchema,
  signupBrandingSchema,
  signupConsentSchema,
  signupDetailsRequestSchema,
  signupDetailsResponseSchema,
  signupDiscordConfirmRequestSchema,
  signupDiscordLinkResponseSchema,
  signupErrorCodeSchema,
  signupErrorSchema,
  signupFieldKeySchema,
  signupFieldKindSchema,
  signupFieldSchema,
  signupFieldsSchema,
  signupIdSchema,
  signupJoinResponseSchema,
  signupPageSchema,
  signupProductSchema,
  signupSlugSchema,
  signupStartRequestSchema,
  signupStartResponseSchema,
  signupStateSchema,
  signupStepSchema,
  signupVerifyEmailRequestSchema,
  signupVerifyEmailResponseSchema,
  tenantHandleSchema,
  tiktokTrackingEventSchema,
  touchConsentRequestSchema,
  touchRequestSchema,
  touchResponseSchema,
  trackingPixelsSchema
};
